import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Input from "../common/Input";

interface Snippet {
  id: string;
  title: string;
  content: string;
  description?: string | null;
}

interface SnippetManagerProps {
  /** Base path for snippet CRUD endpoints (GET/POST on the base, PATCH/DELETE on `${base}/:id`) */
  apiBasePath?: string;
  /** Render without the page-level card chrome (for use inside modals) */
  embedded?: boolean;
}

const SnippetManager: React.FC<SnippetManagerProps> = ({
  apiBasePath = "/api/agency/snippets",
  embedded = false,
}) => {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSnippet, setEditingSnippet] = useState<Snippet | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [snippetToDelete, setSnippetToDelete] = useState<Snippet | null>(null);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    fetchSnippets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBasePath]);

  const fetchSnippets = async () => {
    try {
      const response = await axios.get(apiBasePath);
      setSnippets(response.data.snippets || []);
    } catch (error) {
      console.error("Error fetching snippets:", error);
      toast.error("Failed to fetch snippets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingSnippet(null);
    setTitle("");
    setContent("");
    setDescription("");
    setShowDialog(true);
  };

  const handleEdit = (snippet: Snippet) => {
    setEditingSnippet(snippet);
    setTitle(snippet.title);
    setContent(snippet.content);
    setDescription(snippet.description || "");
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!content.trim()) {
      toast.error("Content is required");
      return;
    }

    try {
      const data = { title, content, description: description || null };

      if (editingSnippet) {
        await axios.patch(`${apiBasePath}/${editingSnippet.id}`, data);
        toast.success("Snippet updated successfully");
      } else {
        await axios.post(apiBasePath, data);
        toast.success("Snippet created successfully");
      }

      await fetchSnippets();
      setShowDialog(false);
    } catch (error) {
      console.error("Error saving snippet:", error);
      toast.error("Failed to save snippet");
    }
  };

  const handleDelete = (snippet: Snippet) => {
    setSnippetToDelete(snippet);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!snippetToDelete) return;

    try {
      await axios.delete(`${apiBasePath}/${snippetToDelete.id}`);
      toast.success("Snippet deleted successfully");
      await fetchSnippets();
    } catch (error) {
      console.error("Error deleting snippet:", error);
      toast.error("Failed to delete snippet");
    } finally {
      setShowDeleteModal(false);
      setSnippetToDelete(null);
    }
  };

  if (loading) {
    return embedded ? (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    ) : (
      <div className="p-6">Loading snippets...</div>
    );
  }

  return (
    <div
      className={
        embedded ? "" : "p-6 card border border-base-300 bg-base-100 mt-8"
      }
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between ${
          embedded ? "mb-4" : "mb-6 pb-6 border-b border-base-300"
        }`}
      >
        {embedded ? (
          <h4 className="font-semibold text-gray-700 dark:text-base-content/80">
            Snippets ({snippets.length})
          </h4>
        ) : (
          <div>
            <h2 className="text-2xl font-semibold text-base-content mb-2">
              Department Snippets
            </h2>
            <p className="text-base-content/60">
              Create reusable text blocks shared with all officers in your
              department.
            </p>
          </div>
        )}
        <Button onClick={handleCreateNew} variant="primary" size="sm">
          <PlusIcon className="w-4 h-4 mr-2" />
          Add Snippet
        </Button>
      </div>

      {/* Snippets List */}
      <div className="space-y-4">
        {snippets.map((snippet) => (
          <div
            key={snippet.id}
            className="p-6 border rounded-lg bg-base-100 border-base-300 hover:border-gray-300 dark:hover:border-base-content/30 transition-all hover:shadow-md"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-base-content mb-1">
                  {snippet.title}
                </h4>
                {snippet.description && (
                  <p className="text-sm text-base-content/60 mb-2">
                    {snippet.description}
                  </p>
                )}
                <p className="text-sm text-gray-600 dark:text-base-content/70 line-clamp-3 whitespace-pre-wrap">
                  {snippet.content}
                </p>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  onClick={() => handleEdit(snippet)}
                  className="p-2 text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(snippet)}
                  className="p-2 text-base-content/40 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {snippets.length === 0 && (
          <div className="text-center text-base-content/60 py-12 bg-base-200 rounded-lg border-2 border-dashed border-gray-300 dark:border-base-300">
            <ClipboardDocumentListIcon className="w-12 h-12 mx-auto mb-4 text-base-content/40" />
            <p className="text-lg mb-2">No department snippets yet</p>
            <p className="text-sm">
              Create snippets that all officers in your department can use in
              their reports.
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <dialog className={`modal ${showDialog ? "modal-open" : ""}`}>
        <div className="modal-box max-w-lg">
          <h3 className="font-bold text-lg">
            {editingSnippet ? "Edit Snippet" : "Add New Snippet"}
          </h3>
          <p className="text-sm text-base-content/60 mt-2 mb-4">
            Use double braces{" "}
            <code className="bg-gray-100 dark:bg-base-200 px-1 rounded">
              {"{{placeholder}}"}
            </code>{" "}
            for fillable placeholders in Title and Content.
          </p>
          <div className="space-y-4">
            <Input
              title="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Snippet title"
              className="w-full !max-w-full"
            />
            <Input
              title="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of when to use this snippet"
              className="w-full !max-w-full"
            />
            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-base-content/80 mb-1 block">
                Content
              </label>
              <textarea
                className="textarea textarea-bordered w-full h-32 focus:outline-none focus:border-blue-500"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Snippet content..."
              />
            </div>
          </div>
          <div className="modal-action">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {editingSnippet ? "Save" : "Add"}
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowDialog(false)}>close</button>
        </form>
      </dialog>

      {/* Delete Confirmation Modal */}
      <dialog className={`modal ${showDeleteModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-md">
          <h3 className="font-bold text-lg">Delete Snippet</h3>
          <p className="py-4 text-gray-600 dark:text-base-content/70">
            Are you sure you want to delete the snippet "
            {snippetToDelete?.title}"? This will remove it for all officers in
            your department.
          </p>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSnippetToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Snippet
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setSnippetToDelete(null);
            }}
          >
            close
          </button>
        </form>
      </dialog>
    </div>
  );
};

export default SnippetManager;
